import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PageShell from "@/components/PageShell";
import { fmtMoney, fmtDate, EmptyState, SkeletonRow } from "@/components/Shared";
import useSmartPoll from "@/lib/useSmartPoll";
import { Clock, MagnifyingGlass } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";

/** Financial admin view — clients that Support marked as pending activation.
 *  Read-only mirror of the Support side. Activation happens on the Support side.
 */
export default function PendingActivationClients() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { status: "pending" };
      if (search) params.search = search;
      const { data } = await api.get("/clients", { params });
      setClients(data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);
  useSmartPoll(load, 60000);

  const total = clients.length;
  const totalMonthly = useMemo(
    () => clients.reduce((s, c) => s + (Number(c.monthly_fee) || 0), 0),
    [clients]
  );

  return (
    <PageShell
      section="Operación"
      page="Pendientes activación"
      context={`${total} ${total === 1 ? "cliente" : "clientes"}`}
    >
      {/* Summary strip */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-200">
          <div className="px-5 py-4 relative">
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-amber-500" />
            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-700 font-semibold flex items-center gap-1.5">
              <Clock size={11} weight="fill" /> Esperando activación
            </div>
            <div className="mt-1.5 text-2xl font-semibold font-mono tabular-nums text-slate-900">{total}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Support debe completar la activación</div>
          </div>
          <div className="px-5 py-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">MRR potencial</div>
            <div className="mt-1.5 text-2xl font-semibold font-mono tabular-nums text-slate-900">{fmtMoney(totalMonthly)}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">al activarse todos</div>
          </div>
          <div className="px-5 py-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Acción</div>
            <div className="mt-1.5 text-sm text-slate-700 leading-tight">Estos clientes están en la cola de <span className="font-semibold">Support</span>.</div>
            <div className="text-[11px] text-slate-500 mt-1">Cuando Support los active, se moverán a Clientes activos automáticamente.</div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white border border-slate-200 rounded-md mb-4">
        <div className="px-4 py-3">
          <div className="relative max-w-md">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, email o dirección..."
              className="pl-9 h-10 border-slate-200"
              data-testid="pending-search"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">Cliente</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">Contacto</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">Dirección</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 text-right">Tarifa</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">Registrado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} cols={5} />)}
            {!loading && clients.length === 0 && (
              <tr><td colSpan={5}>
                <EmptyState
                  icon={Clock}
                  title="Sin pendientes"
                  message="No hay clientes esperando activación en este momento."
                />
              </td></tr>
            )}
            {clients.map((c) => (
              <tr key={c.id} className="hover:bg-slate-50 transition-colors" data-testid={`pending-row-${c.id}`}>
                <td className="px-5 py-3.5 font-medium text-slate-900">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
                      <Clock size={9} weight="fill" /> Pendiente
                    </span>
                    <span className="truncate">{c.name}</span>
                  </div>
                </td>
                <td className="px-5 py-3.5 text-slate-600">
                  <div className="truncate max-w-[200px]">{c.email || "—"}</div>
                  <div className="text-xs text-slate-500 font-mono">{c.phone || ""}</div>
                </td>
                <td className="px-5 py-3.5 text-slate-600 max-w-xs truncate">{c.address || "—"}</td>
                <td className="px-5 py-3.5 text-right font-mono tabular-nums text-slate-900">{fmtMoney(c.monthly_fee)}</td>
                <td className="px-5 py-3.5 text-xs text-slate-500 font-mono">{c.created_at ? fmtDate(c.created_at) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PageShell>
  );
}
